import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from 'src/services';
import { ServiceRequest, ServiceRequestType, Tag } from 'src/models';
import {
  UseInfiniteQueryOverrideOptions,
  UseQueryOverrideOptions,
} from '../common';

export interface UseGetServiceRequestListParams {
  type?: ServiceRequestType['service_request_type'];
  conditionId?: string;
  patientId?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
  tagIds?: Tag['id'][];
  status_ids?: number[];
  chat_completion_status?: string[];
}

const defaultPageSize = 50;

export type GetServiceRequestListResponse = {
  page: number;
  pages: number;
  service_requests: ServiceRequest[];
  per_page: number;
  total: number;
};

export const useGetServiceRequestList = (
  params?: UseGetServiceRequestListParams,
  options: UseQueryOverrideOptions = {}
) => {
  const {
    type,
    conditionId,
    patientId,
    sortBy,
    sortDirection,
    page,
    perPage,
    tagIds,
    status_ids,
    chat_completion_status,
  } = params || {};
  return useQuery({
    ...options,
    queryKey: ['serviceRequestList', params],
    queryFn: () => {
      return api
        .get<GetServiceRequestListResponse>('/v3/service_request', {
          params: {
            type,
            condition_id: conditionId,
            patient_id: patientId,
            sort_by: sortBy,
            sort_direction: sortDirection,
            page: page || 1,
            per_page: perPage || defaultPageSize,
            tag_ids: tagIds?.length ? tagIds.join(',') : undefined,
            status_id: status_ids?.length ? status_ids.join(',') : undefined,
            chat_completion_status: chat_completion_status?.length
              ? chat_completion_status.join(',')
              : undefined,
          },
        })
        .then(({ data: { service_requests, ...rest } }) => ({
          ...rest,
          serviceRequests: service_requests,
        }));
    },
  });
};

export const useGetServiceRequestInfiniteList = (
  params?: UseGetServiceRequestListParams,
  options: UseInfiniteQueryOverrideOptions = {}
) => {
  return useInfiniteQuery({
    ...options,
    queryKey: ['serviceRequestList', 'infinite', params],
    queryFn: ({ pageParam }) => {
      return api
        .get<GetServiceRequestListResponse>('/v3/service_request', {
          params: {
            type: pageParam.type,
            condition_id: pageParam.conditionId,
            patient_id: pageParam.patientId,
            sort_by: pageParam.sortBy,
            sort_direction: pageParam.sortDirection,
            page: pageParam.page,
            per_page: pageParam.perPage,
            tag_ids: pageParam.tagIds?.length
              ? pageParam.tagIds.join(',')
              : undefined,
            status_id: pageParam.status_ids?.length
              ? pageParam.status_ids.join(',')
              : undefined,
            chat_completion_status: pageParam.chat_completion_status?.length
              ? pageParam.chat_completion_status.join(',')
              : undefined,
          },
        })
        .then(({ data: { service_requests, ...rest } }) => ({
          ...rest,
          serviceRequests: service_requests,
        }));
    },
    getNextPageParam: (lastPage) => {
      return lastPage.page < lastPage.pages
        ? {
            perPage: defaultPageSize,
            ...params,
            page: lastPage.page + 1,
          }
        : undefined;
    },
    initialPageParam: {
      page: 1,
      perPage: defaultPageSize,
      ...params,
    },
  });
};
